import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import logo from '../assets/images/logo.svg';

import { API_BASE_URL } from '../config';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogPost();
  }, [id]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/blogs/${id}`);
      setBlog(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Blog post not found');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f7911d] mx-auto mb-4"></div>
          <p className="text-[#4f4f4f]">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Blog post not found'}</p>
          <button
            onClick={() => navigate('/community')}
            className="px-6 py-2 bg-[#f7911d] text-white rounded-lg hover:bg-[#e67e0a]"
          >
            ← Back to Community
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="bg-white border-b border-[#e5e7eb] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <img
              src={logo}
              alt="CI Agent Logo"
              className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleLogoClick}
            />
            <nav className="flex items-center text-sm font-medium text-[#4f4f4f] gap-6">
              <button onClick={() => navigate('/community')} className="hover:text-[#f7911d] transition">
                ← Back to Community
              </button>
              <button onClick={() => navigate('/login')} className="hover:text-[#f7911d] transition">
                Sign in
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Blog Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Category Badge */}
        <div className="flex items-center space-x-2 text-sm text-[#4f4f4f] mb-4">
          <span className="bg-[#f7911d] text-white px-3 py-1 rounded-full text-xs font-medium">
            {blog.category}
          </span>
          <span>•</span>
          <span>{blog.read_time_minutes} min read</span>
          <span>•</span>
          <span>{new Date(blog.published_at || blog.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-[#0a0908] mb-6 leading-tight">
          {blog.title}
        </h1>

        {/* Author Info */}
        <div className="flex items-center space-x-3 mb-8 pb-8 border-b border-[#e5e7eb]">
          <div className="w-12 h-12 bg-[#f7911d] rounded-full flex items-center justify-center text-white text-lg font-medium">
            {blog.author_name ? blog.author_name.split(' ').map(n => n[0]).join('') : 'AI'}
          </div>
          <div>
            <p className="text-sm font-medium text-[#0a0908]">{blog.author_name || 'ALICE Team'}</p>
            <p className="text-xs text-[#4f4f4f]">Published on {new Date(blog.published_at || blog.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Featured Image */}
        {blog.featured_image && (
          <div className="relative w-full h-96 rounded-lg mb-8 bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center overflow-hidden">
            <img
              src={blog.featured_image}
              alt={blog.featured_image_alt || blog.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                // If image fails to load, show placeholder
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `
                  <div class="text-center">
                    <svg class="w-20 h-20 mx-auto text-orange-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p class="text-orange-400 text-sm">Featured image</p>
                  </div>
                `;
              }}
            />
          </div>
        )}

        {/* Blog Content */}
        <style>
          {`
            .blog-content h1, .blog-content h2, .blog-content h3 {
              color: #0a0908 !important;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1rem;
            }
            .blog-content h1 { font-size: 2.25rem; }
            .blog-content h2 { font-size: 1.875rem; }
            .blog-content h3 { font-size: 1.5rem; }
            .blog-content p {
              margin-bottom: 1.25rem;
              line-height: 1.8;
              color: #0a0908;
            }
            .blog-content ul, .blog-content ol {
              margin-bottom: 1.25rem;
              padding-left: 1.5rem;
            }
            .blog-content li {
              margin-bottom: 0.5rem;
            }
            .blog-content a {
              color: #f7911d !important;
              text-decoration: underline;
            }
            .blog-content a:hover {
              color: #e67e0a !important;
            }
            .blog-content strong {
              font-weight: 600;
              color: #0a0908;
            }
            .blog-content blockquote {
              border-left: 4px solid #f7911d;
              padding-left: 1rem;
              font-style: italic;
              color: #4f4f4f;
              margin: 1.5rem 0;
            }
            .blog-content code {
              background-color: #f3f4f6;
              padding: 0.2rem 0.4rem;
              border-radius: 0.25rem;
              font-family: monospace;
              font-size: 0.9em;
            }
            .blog-content pre {
              background-color: #f3f4f6;
              padding: 1rem;
              border-radius: 0.5rem;
              overflow-x: auto;
              margin: 1.5rem 0;
            }
            .blog-content img {
              max-width: 100%;
              height: auto;
              border-radius: 0.5rem;
              margin: 1.5rem 0;
            }
          `}
        </style>
        <div
          className="prose prose-lg max-w-none mb-8 blog-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="border-t border-[#e5e7eb] pt-8 mb-8">
            <h3 className="text-sm font-medium text-[#4f4f4f] mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-[#4f4f4f] rounded-full text-sm hover:bg-gray-200 cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Back to Community Button */}
        <div className="border-t border-[#e5e7eb] pt-8 text-center">
          <button
            onClick={() => navigate('/community')}
            className="px-8 py-3 bg-[#f7911d] text-white rounded-lg hover:bg-[#e67e0a] font-medium"
          >
            ← Back to Community
          </button>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-10 mt-10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8 px-6">
          <div>
            <img src={logo} alt="CI Agent Logo" className="h-8 mb-2" />
            <div className="text-gray-500 text-sm">© 2025 CI Agent</div>
          </div>
          <div className="flex flex-col md:flex-row gap-8 flex-1 justify-between">
            <div>
              <div className="font-semibold text-gray-700 mb-2">Company</div>
              <ul className="text-gray-500 text-sm space-y-1">
                <li><button className="hover:text-orange-400 transition text-left">About us</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Contact us</button></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-2">Community</div>
              <ul className="text-gray-500 text-sm space-y-1">
                <li><button onClick={() => navigate('/community')} className="hover:text-orange-400 transition text-left">Blog</button></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="text-center text-xs text-gray-400 mt-8">Less structure, more intelligence.</div>
      </footer>
    </div>
  );
};

export default BlogPost;

